import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/Button";
import { CommandBlock } from "@/components/CommandBlock";
import { DatePicker } from "@/components/DatePicker";
import { Modal, ModalTitle } from "@/components/Modal";
import { Select } from "@/components/Select";
import { TextInput } from "@/components/TextInput";
import { useApi } from "@/lib/api";
import {
  formatIsoDate,
  localDayEnd,
  parseIsoDate,
  startOfDay,
} from "@/lib/calendarDates";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";
import {
  DEFAULT_LIFETIME_DAYS,
  describePermission,
  LIFETIME_CHOICES,
  type PermissionGroup,
  permissionsFor,
  permissionsInGroup,
  type Token,
  TOKEN_TYPES,
  type TokenType,
  tokenExpiresAt,
} from "./shared";

interface CreateTokenResponse {
  token: Token;
  rawToken: string;
}

interface TokenPolicy {
  maxLifetimeDays: number;
  latestExpiry: string;
}

interface TeamOption {
  id: string;
  name: string;
}

const GROUP_LABEL: Record<PermissionGroup, string> = {
  read: "Can read",
  change: "Can change",
};

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);

  return next;
}

export function CreateTokenModal({
  teams,
  canCreateOrganizationTokens,
  onClose,
  onCreated,
  showToast,
}: {
  teams: TeamOption[];
  canCreateOrganizationTokens: boolean;
  onClose: () => void;
  onCreated: () => void;
  showToast: (message: string, variant: "success" | "error") => void;
}) {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const { copiedField, copyToClipboard } = useCopyToClipboard();

  const today = useMemo(() => startOfDay(new Date()), []);
  const [policy, setPolicy] = useState<TokenPolicy | null>(null);
  const [tokenType, setTokenType] = useState<TokenType>(
    teams.length > 0 ? TOKEN_TYPES.team : TOKEN_TYPES.organization,
  );
  const [name, setName] = useState("");
  const [teamId, setTeamId] = useState(teams[0]?.id ?? "");
  const [permissions, setPermissions] = useState<string[]>([]);
  const [expiresOn, setExpiresOn] = useState(() =>
    formatIsoDate(addDays(today, DEFAULT_LIFETIME_DAYS)),
  );
  const [creating, setCreating] = useState(false);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);

  const isOrganizationToken = tokenType === TOKEN_TYPES.organization;
  const latestSelectableDate = useMemo(() => {
    if (!policy) {
      return null;
    }

    return startOfDay(new Date(policy.latestExpiry));
  }, [policy]);

  const expiresAt = tokenExpiresAt(
    localDayEnd(expiresOn),
    policy?.latestExpiry,
  );

  const lifetimeChoices = LIFETIME_CHOICES.filter(
    (days) => !policy || days <= policy.maxLifetimeDays,
  );

  const chosenLifetime = useMemo(() => {
    const date = parseIsoDate(expiresOn);

    if (!date) {
      return "custom";
    }

    const days = Math.round(
      (date.getTime() - today.getTime()) / (24 * 60 * 60 * 1000),
    );

    return LIFETIME_CHOICES.some((choice) => choice === days)
      ? String(days)
      : "custom";
  }, [expiresOn, today]);

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    void api
      .get<TokenPolicy>(`/organizations/${orgId}/tokens/policy`)
      .then((data) => {
        if (cancelled || !data) {
          return;
        }

        setPolicy(data);

        const latest = startOfDay(new Date(data.latestExpiry));
        const preferred = addDays(today, DEFAULT_LIFETIME_DAYS);

        setExpiresOn(formatIsoDate(preferred > latest ? latest : preferred));
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, api, orgId, today]);

  function changeTokenType(next: TokenType) {
    setTokenType(next);
    setPermissions((current) =>
      current.filter((permission) =>
        permissionsFor(next).some((entry) => entry.value === permission),
      ),
    );
  }

  function togglePermission(permission: string) {
    setPermissions((current) =>
      current.includes(permission)
        ? current.filter((entry) => entry !== permission)
        : [...current, permission],
    );
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();

    if (!accessToken || !orgId || !expiresAt) {
      return;
    }

    setCreating(true);

    try {
      const body = await api.send<CreateTokenResponse>(
        `/organizations/${orgId}/tokens/${isOrganizationToken ? "organization" : "team"}`,
        "POST",
        isOrganizationToken
          ? { name, permissions, expiresAt }
          : { name, teamId, permissions, expiresAt },
        "Couldn't create token.",
      );

      setRevealedToken(body.rawToken);
      onCreated();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't create token.",
        "error",
      );
    } finally {
      setCreating(false);
    }
  }

  if (revealedToken) {
    return (
      <Modal
        onClose={onClose}
        closeOnBackdropClick={false}
        closeOnEscape={false}
        scrollable={false}
      >
        <ModalTitle className="mb-4 shrink-0">Token created</ModalTitle>
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <CommandBlock
            className="mb-1.5"
            command={revealedToken}
            field="token"
            wrap
            copiedField={copiedField}
            onCopy={copyToClipboard}
          />
          <p className="text-xs text-foreground/70">
            Copy this token now and store it securely. You won't be able to view
            it again.
          </p>
        </div>
        <div className="mt-4 flex shrink-0 justify-end">
          <Button onClick={onClose} variant="primary">
            Done
          </Button>
        </div>
      </Modal>
    );
  }

  function renderGroup(group: PermissionGroup) {
    const options = permissionsInGroup(tokenType, group);

    if (options.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-col gap-1.5">
        <span
          className={`text-2xs font-semibold uppercase tracking-wider ${
            group === "change" ? "text-warning" : "text-subtle"
          }`}
        >
          {GROUP_LABEL[group]}
        </span>
        <div className="flex flex-col">
          {options.map((permission, index) => (
            <div
              key={permission.value}
              className={
                index < options.length - 1 ? "border-b border-surface" : ""
              }
            >
              <label className="flex min-h-11 w-fit cursor-pointer items-center gap-3 py-1.5">
                <input
                  type="checkbox"
                  checked={permissions.includes(permission.value)}
                  onChange={() => togglePermission(permission.value)}
                  className={`h-4 w-4 shrink-0 ${
                    group === "change" ? "accent-warning" : "accent-accent"
                  }`}
                />
                <span className="min-w-0">
                  <span className="block text-sm text-foreground">
                    {permission.label}
                  </span>
                  <span className="block text-xs text-foreground/70">
                    {describePermission(permission, tokenType)}
                  </span>
                </span>
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Modal onClose={onClose} scrollable={false}>
      <ModalTitle className="mb-4 shrink-0">Create a token</ModalTitle>
      <form onSubmit={handleCreate} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1.5">
              <label htmlFor="tokenName" className="text-sm text-foreground">
                Name
              </label>
              <TextInput
                id="tokenName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                className="w-full"
              />
            </div>
            <div className="flex w-48 flex-col gap-1.5">
              <label htmlFor="tokenScope" className="text-sm text-foreground">
                Scope
              </label>
              <Select
                id="tokenScope"
                value={isOrganizationToken ? TOKEN_TYPES.organization : teamId}
                onChange={(e) => {
                  if (e.target.value === TOKEN_TYPES.organization) {
                    changeTokenType(TOKEN_TYPES.organization);
                    return;
                  }

                  changeTokenType(TOKEN_TYPES.team);
                  setTeamId(e.target.value);
                }}
                className="w-full"
              >
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
                {canCreateOrganizationTokens && (
                  <option value={TOKEN_TYPES.organization}>Organization</option>
                )}
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex gap-3">
              {lifetimeChoices.length > 0 && (
                <div className="flex w-36 flex-col gap-1.5">
                  <label
                    htmlFor="tokenLifetime"
                    className="text-sm text-foreground"
                  >
                    Expires in
                  </label>
                  <Select
                    id="tokenLifetime"
                    value={chosenLifetime}
                    onChange={(e) => {
                      if (e.target.value === "custom") {
                        return;
                      }

                      setExpiresOn(
                        formatIsoDate(addDays(today, Number(e.target.value))),
                      );
                    }}
                    className="w-full"
                  >
                    {lifetimeChoices.map((days) => (
                      <option key={days} value={days}>
                        {days} days
                      </option>
                    ))}
                    {chosenLifetime === "custom" && (
                      <option value="custom">Custom</option>
                    )}
                  </Select>
                </div>
              )}
              <div className="flex flex-1 flex-col gap-1.5">
                <label
                  htmlFor="tokenExpiry"
                  className="text-sm text-foreground"
                >
                  Expires on
                </label>
                <DatePicker
                  id="tokenExpiry"
                  value={expiresOn}
                  onChange={setExpiresOn}
                  minDate={today}
                  maxDate={latestSelectableDate ?? undefined}
                  label="Choose an expiry date"
                />
              </div>
            </div>
            <span className="text-xs text-foreground/70">
              Tokens can have a maximum lifetime of 365 days.
            </span>
          </div>

          {renderGroup("read")}
          {renderGroup("change")}

          {permissions.length === 0 && (
            <p className="text-xs leading-relaxed text-foreground/70">
              At least one permission is required.
            </p>
          )}
        </div>

        <div className="mt-4 flex shrink-0 justify-end gap-3">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={
              creating ||
              permissions.length === 0 ||
              !expiresAt ||
              (!isOrganizationToken && !teamId)
            }
          >
            {creating ? "Creating..." : "Create token"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
